import type { UserRole, Permission, OrganizationStatus } from '@/types/models';
import { UserRole as UserRoleEnum } from '@/types/models';
import type { BackendUserResponse } from '@/types/auth';

/** Highest-privilege role first — used for primary role resolution in the UI */
export const ROLE_PRIORITY: UserRole[] = [
  UserRoleEnum.PLATFORM_ADMIN,
  UserRoleEnum.ORG_ADMIN,
  UserRoleEnum.MANAGER,
  UserRoleEnum.TIER3,
  UserRoleEnum.TIER2,
  UserRoleEnum.TIER1,
];

/** Short display labels for badges, headers, and profile UI */
export const ROLE_LABELS: Record<UserRole, string> = {
  platform_admin: 'Platform Admin',
  org_admin: 'Org Admin',
  manager: 'Manager',
  tier3: 'Senior Analyst',
  tier2: 'Mid-Level Analyst',
  tier1: 'Junior Analyst',
};

export function getPrimaryRole(roles: string[]): UserRole | null {
  for (const role of ROLE_PRIORITY) {
    if (roles.includes(role)) return role;
  }
  return null;
}

const ROLE_HIERARCHY: UserRole[] = [
  UserRoleEnum.TIER1,
  UserRoleEnum.TIER2,
  UserRoleEnum.TIER3,
  UserRoleEnum.MANAGER,
  UserRoleEnum.ORG_ADMIN,
  UserRoleEnum.PLATFORM_ADMIN,
];

/**
 * Role-based permissions mapping
 * Defines which permissions each role has
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  platform_admin: [
    'manage_own_organization',
    'view_own_organization',
    'approve_organizations',
    'suspend_organizations',
    'manage_own_org_users',
    'manage_platform_users',
    'view_own_org_users',
    'view_platform_users',
    'assign_roles_own_org',
    'assign_platform_roles',
    'create_asset',
    'edit_asset',
    'delete_asset',
    'view_assets',
    'ip_lookup',
    'view_vulnerabilities',
  ],
  org_admin: [
    'manage_own_organization',
    'view_own_organization',
    'manage_own_org_users',
    'view_own_org_users',
    'assign_roles_own_org',
    'create_asset',
    'edit_asset',
    'delete_asset',
    'view_assets',
    'ip_lookup',
    'view_vulnerabilities',
  ],
  // manager is a peer of org_admin (see SYSTEM_ROLES in
  // packages/auth-common), not a scaled-down variant — identical grant.
  manager: [
    'manage_own_organization',
    'view_own_organization',
    'manage_own_org_users',
    'view_own_org_users',
    'assign_roles_own_org',
    'create_asset',
    'edit_asset',
    'delete_asset',
    'view_assets',
    'ip_lookup',
    'view_vulnerabilities',
  ],
  tier3: [
    'view_own_organization',
    'view_own_org_users',
    'view_assets',
    'view_vulnerabilities',
  ],
  tier2: [
    'view_own_organization',
    'view_own_org_users',
    'view_assets',
    'view_vulnerabilities',
  ],
  tier1: ['view_own_organization', 'view_assets', 'view_vulnerabilities'],
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  const permissions = ROLE_PERMISSIONS[role];
  return permissions?.includes(permission) ?? false;
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(
  role: UserRole,
  permissions: Permission[],
): boolean {
  return permissions.some((permission) => hasPermission(role, permission));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(
  role: UserRole,
  permissions: Permission[],
): boolean {
  return permissions.every((permission) => hasPermission(role, permission));
}

/**
 * Check if a user has a specific role
 */
export function hasRole(
  user: BackendUserResponse | null,
  role: UserRole,
): boolean {
  return user?.role === role;
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(
  user: BackendUserResponse | null,
  roles: UserRole[],
): boolean {
  if (!user) return false;
  return roles.includes(user.role);
}

/**
 * Check if user role is at least the specified minimum role
 * (i.e., has equal or higher privilege level)
 */
export function hasMinimumRole(
  user: BackendUserResponse | null,
  minimumRole: UserRole,
): boolean {
  if (!user) return false;
  const userRoleIndex = ROLE_HIERARCHY.indexOf(user.role);
  const minRoleIndex = ROLE_HIERARCHY.indexOf(minimumRole);
  return userRoleIndex >= minRoleIndex;
}

/**
 * Check if organization is in a specific status
 */
export function hasOrganizationStatus(
  status: OrganizationStatus | undefined,
  requiredStatus: OrganizationStatus,
): boolean {
  return status === requiredStatus;
}

/**
 * Whether an organization may use the platform (approved or active status).
 */
export function isOrganizationActive(
  status: OrganizationStatus | undefined,
): boolean {
  return status === 'approved' || status === 'active';
}

/**
 * Check if user can access the platform
 * User must be active and have an approved/active organization (except platform_admin)
 */
export function canAccessPlatform(
  user: BackendUserResponse | null,
  orgStatus?: OrganizationStatus,
): boolean {
  if (!user || !user.isActive) return false;

  // Platform admins can always access
  if (user.role === UserRoleEnum.PLATFORM_ADMIN) return true;

  // Other users need approved organization
  return isOrganizationActive(orgStatus);
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(role: UserRole): Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

/**
 * Get human-readable role name
 */
export function getRoleName(role: UserRole): string {
  return ROLE_LABELS[role] ?? role;
}

/**
 * Get organization status display name
 */
export function getOrganizationStatusName(status: OrganizationStatus): string {
  const statusNames: Record<OrganizationStatus, string> = {
    pending: 'Pending Approval',
    approved: 'Approved',
    active: 'Active',
    suspended: 'Suspended',
    rejected: 'Rejected',
  };
  return statusNames[status] ?? status;
}

/**
 * Get organization status color class (for UI)
 */
export function getOrganizationStatusColor(status: OrganizationStatus): string {
  const colorMap: Record<OrganizationStatus, string> = {
    pending:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    approved:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    suspended: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };
  return colorMap[status] ?? 'bg-gray-100 text-gray-800';
}

